// src/App.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import LoginScreen from "./components/LoginScreen";
import Dashboard from "./components/Dashboard";
import { Purchase, Production, Transfer, Sales, Reports, Prices } from "./components/Modules";
import { History } from "./components/History";
import { OpeningStock } from "./components/OpeningStock";
import { ShopInventory } from "./components/ShopInventory";
import { CreditTracker } from "./components/CreditTracker";
import { useAppState } from "./hooks/useAppState";

const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

const TABS_CONFIG = [
  { id: "dashboard",  icon: "🏠", label: "Home",     plant: true,  shop: true,  admin: true  },
  { id: "purchase",   icon: "📦", label: "Purchase",  plant: true,  shop: false, admin: true  },
  { id: "production", icon: "⚙️", label: "Produce",   plant: true,  shop: false, admin: true  },
  { id: "transfer",   icon: "🚛", label: "Transfer",  plant: true,  shop: false, admin: true  },
  { id: "sales",      icon: "💰", label: "Sales",     plant: true,  shop: true,  admin: true  },
  { id: "reports",    icon: "📊", label: "Reports",   plant: true,  shop: true,  admin: true  },
  { id: "history",    icon: "📋", label: "History",   plant: true,  shop: true,  admin: true  },
  { id: "shop",       icon: "🏪", label: "Shop",      plant: true,  shop: true,  admin: true  },
  { id: "credit",     icon: "💳", label: "Credit",    plant: true,  shop: true,  admin: true  },
  { id: "opening",    icon: "📥", label: "Opening",   plant: false, shop: false, admin: true  },
  { id: "prices",     icon: "🏷️", label: "Prices",    plant: false, shop: false, admin: true  },
];

const TAB_NAMES = {
  dashboard:  "Live Dashboard",
  purchase:   "Purchase Log",
  production: "Production Batch",
  transfer:   "Plant → Shop Transfer",
  sales:      "Sales Entry",
  reports:    "Reports",
  history:    "Edit / Delete Entries",
  shop:       "Shop Inventory (SKU-wise)",
  credit:     "Credit & Outstanding Dues",
  opening:    "Opening Stock Entry",
  prices:     "Price Management",        // ✅ FIX 1: was missing, caused "undefined" in top bar
};

// ✅ FIX 2: Logo now references /public/logo.png (local file, not imgbb.com)
// Copy the Leap logo image to public/logo.png in your project before deploying.
const LOGO_SRC = "/logo.png";

export default function App() {
  const [user, setUser] = useState(null);
  const [tab, setTab]   = useState("dashboard");
  const [showTimeout, setShowTimeout] = useState(false);
  const { state, loading, syncing, lastSync, error, refresh, actions } = useAppState();
  const timerRef = useRef(null);
  const warnRef  = useRef(null);

  const logout = useCallback(() => {
    setUser(null);
    setShowTimeout(false);
    clearTimeout(timerRef.current);
    clearTimeout(warnRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    clearTimeout(warnRef.current);
    setShowTimeout(false);
    // Show warning at 14 minutes
    warnRef.current = setTimeout(() => setShowTimeout(true), TIMEOUT_MS - 60 * 1000);
    // Auto logout at 15 minutes
    timerRef.current = setTimeout(() => logout(), TIMEOUT_MS);
  }, [logout]);

  // Start timer when user logs in
  useEffect(() => {
    if (!user) return;
    resetTimer();
    const events = ["click", "touchstart", "keydown", "scroll"];
    events.forEach(e => window.addEventListener(e, resetTimer));
    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(warnRef.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, resetTimer]);

  if (!user) return <LoginScreen onLogin={u => { setUser(u); setTab("dashboard"); }} logoSrc={LOGO_SRC} />;

  const availableTabs = TABS_CONFIG.filter(t =>
    (t.plant && user.plant) || (t.shop && user.shop) || (t.admin && user.admin)
  );

  const renderTab = () => {
    switch (tab) {
      case "dashboard":  return <Dashboard state={state} />;
      case "purchase":   return <Purchase  state={state} actions={actions} user={user} />;
      case "production": return <Production state={state} actions={actions} user={user} />;
      case "transfer":   return <Transfer  state={state} actions={actions} user={user} />;
      case "sales":      return <Sales     state={state} actions={actions} user={user} />;
      case "reports":    return <Reports   state={state} />;
      case "history":    return <History      state={state} actions={actions} user={user} />;
      case "shop":       return <ShopInventory  state={state} />;
      case "credit":     return <CreditTracker  state={state} actions={actions} user={user} />;
      case "opening":    return <OpeningStock    state={state} actions={actions} user={user} />;
      case "prices":     return <Prices    state={state} actions={actions} user={user} />;
      default:           return <Dashboard state={state} />;
    }
  };

  return (
    <div style={{
      fontFamily: "'DM Sans', sans-serif",
      maxWidth: 430,
      margin: "0 auto",
      background: "#F7F4EE",
      minHeight: "100vh",
      paddingBottom: 100,
    }}>
      {/* Top bar */}
      <div style={{
        background: "#27500A",
        padding: "14px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky", top: 0, zIndex: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* ✅ FIX 2: Using local logo path */}
          <img
            src={LOGO_SRC}
            alt="Leap — Towards Better Health"
            style={{ height: 38, width: 38, borderRadius: 6, objectFit: "contain", background: "#fff", padding: 2 }}
          />
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}><img src="/logo.jpg" alt="logo" style={{ height:36, width:36, borderRadius:6, objectFit:"contain", background:"#fff", padding:2 }} />
<div style={{ color: "#C0DD97", fontSize: 17, fontWeight: 700 }}>Dharani Group</div></div>
            <div style={{ color: "#7CB85A", fontSize: 11, marginTop: 2 }}>{TAB_NAMES[tab]}</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ background: "#3B6D11", color: "#C0DD97", fontSize: 11, padding: "3px 10px", borderRadius: 12 }}>
            {user.name}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 4, justifyContent: "flex-end" }}>
            {syncing && <span style={{ color: "#7CB85A", fontSize: 10 }}>⟳ syncing</span>}
            {error && <span style={{ color: "#FAC775", fontSize: 10 }}>⚠ offline</span>}
            <span style={{ color: "#7CB85A", fontSize: 10, cursor: "pointer" }}
              onClick={logout}>
              ← logout
            </span>
          </div>
        </div>
      </div>

      {/* Session timeout warning */}
      {showTimeout && (
        <div style={{
          background: "#FFF3CD", borderBottom: "1px solid #FAC775",
          padding: "10px 14px", fontSize: 13, color: "#633806",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          zIndex: 50,
        }}>
          <span>⏱ Logging out in 1 minute due to inactivity</span>
          <button onClick={resetTimer}
            style={{ background: "#27500A", color: "#C0DD97", border: "none",
                     borderRadius: 6, padding: "4px 12px", fontSize: 12,
                     cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
            Stay logged in
          </button>
        </div>
      )}

      {/* Sync error banner */}
      {error && (
        <div style={{
          background: "#FFF3CD", borderBottom: "1px solid #FAC775",
          padding: "8px 14px", fontSize: 12, color: "#633806",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span>⚠ {error}</span>
          <button onClick={refresh}
            style={{ background: "none", border: "none", color: "#633806", cursor: "pointer", fontSize: 12 }}>
            Retry
          </button>
        </div>
      )}

      {/* Loading skeleton */}
      {loading ? (
        <div style={{ padding: "40px 14px", textAlign: "center", color: "#888" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🌿</div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}><img src="/logo.jpg" alt="logo" style={{ height:36, width:36, borderRadius:6, objectFit:"contain", background:"#fff", padding:2 }} /><div style={{ color: "#C0DD97", fontSize: 17, fontWeight: 700 }}>Dharani Group</div></div>
          <div style={{ fontSize: 12, color: "#aaa", marginTop: 6 }}>Connecting to Google Sheets</div>
        </div>
      ) : (
        <div style={{ overflowY: "auto" }}>
          {renderTab()}
        </div>
      )}

      {/* Bottom navigation — 2 rows */}
      <div style={{
        position: "fixed", bottom: 0,
        left: "50%", transform: "translateX(-50%)",
        width: "100%", maxWidth: 430,
        background: "#fff", borderTop: "1px solid #E8E4DC",
        zIndex: 100,
      }}>
        {[0, 1].map(rowIdx => {
          const half = Math.ceil(availableTabs.length / 2);
          const rowTabs = availableTabs.slice(rowIdx * half, rowIdx * half + half);
          return (
            <div key={rowIdx} style={{
              display: "flex",
              borderTop: rowIdx === 1 ? "1px solid #F0EDE6" : "none",
            }}>
              {rowTabs.map(t => (
                <div key={t.id}
                  onClick={() => setTab(t.id)}
                  style={{
                    flex: 1, padding: "6px 4px", textAlign: "center", cursor: "pointer",
                    color: tab === t.id ? "#27500A" : "#aaa",
                    fontWeight: tab === t.id ? 700 : 400,
                    fontSize: 9,
                    background: tab === t.id ? "#F0F9E8" : "transparent",
                  }}>
                  <span style={{ fontSize: 16, display: "block", marginBottom: 1 }}>{t.icon}</span>
                  {t.label}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
